import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";

/**
 * The site's contact details — support email and WhatsApp number — as set by admin in Settings.
 *
 * Fetched **once per page load and shared**. The footer renders inside every layout and on every
 * public page, so a naive `useEffect` fetch per instance would put a request on the wire for each
 * one, on every navigation. The in-flight promise is cached too, so several footers mounting in
 * the same tick queue behind one request rather than racing.
 *
 * Falls back to the same defaults the server uses. The footer is not worth a blank space or an
 * "undefined" mailto if the config request fails.
 */
// `company_name` matches SITE_DEFAULTS server-side. `address` has no sensible default — it is
// the registered address, and inventing one would be worse than the page omitting the block.
const FALLBACK = { whatsapp: "919876543210", support_email: "admin@dharmatn.in", company_name: "DharmaTN" };

let cached = null;
let inFlight = null;

export function loadSiteConfig() {
  if (cached) return Promise.resolve(cached);
  if (!inFlight) {
    inFlight = api.get("/site-config")
      .then(r => { cached = { ...FALLBACK, ...(r.data || {}) }; return cached; })
      .catch(() => FALLBACK)
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

/** Admin just saved new details — drop the cache so the next read is fresh. */
export function clearSiteConfigCache() {
  cached = null;
}

export function useSiteConfig() {
  const [cfg, setCfg] = useState(cached || FALLBACK);
  useEffect(() => {
    let alive = true;
    loadSiteConfig().then(c => { if (alive) setCfg(c); });
    return () => { alive = false; };
  }, []);
  return cfg;
}
